/**
 * Session-scoped counters for how the user has been *using* the map, so a black-map
 * episode can be attributed to a usage pattern rather than guessed at from log
 * co-occurrence.
 *
 * Motivating asymmetry: in CAD the map lives in its own dedicated window and is
 * effectively never navigated away from, while in First Responder the map is mounted
 * for the whole session and merely hidden/shown as the user moves between pages
 * (see fr-esri-map-window-enabler). FR reports far more black maps, so navigation
 * churn and hidden-vs-visible time are the differences worth measuring.
 *
 * Lives in `monitoring` (not `map-window`) because the esri layer may not import
 * from `map-window`, but both may import from here.
 *
 * Plain module state — cheap to read, no PII.
 */

let mapNavigationCount = 0;
let mapVisibleMs = 0;
let mapHiddenMs = 0;
/** null until the map first reports its visibility. */
let isMapHidden: boolean | null = null;
let lastVisibilityChangeMs = Date.now();

let reconnectCount = 0;
let lastReconnectMs: number | null = null;

export type MapLifecycleStats = {
    /** Times the user has navigated TO the map this session (FR only in practice). */
    mapNavigationCount: number;
    /** Accumulated ms the map has been visible. */
    mapVisibleMs: number;
    /** Accumulated ms the map has been mounted but hidden. */
    mapHiddenMs: number;
    /** Whether the map was hidden at the moment this snapshot was taken. A black map
     * that happens while hidden is a materially different failure than one the user
     * is looking at. Undefined when visibility is not tracked (e.g. CAD). */
    wasMapHiddenAtFailure?: boolean;
    /** Reconnects observed by the map this session. */
    mapReconnectCount: number;
    /** Time since the last reconnect-driven refetch, for per-episode reconnect
     * proximity. Undefined if no reconnect has happened. */
    msSinceLastReconnect?: number;
};

/** Call when the user navigates to the map route. */
export function markMapRouteEntered(): void {
    mapNavigationCount++;
}

/**
 * Call whenever the map's visibility changes. Accrues time into the visible or
 * hidden bucket based on the state it is leaving.
 */
export function setMapHidden(hidden: boolean): void {
    const now = Date.now();
    if (isMapHidden !== null) {
        const elapsed = now - lastVisibilityChangeMs;
        if (isMapHidden) {
            mapHiddenMs += elapsed;
        } else {
            mapVisibleMs += elapsed;
        }
    }
    isMapHidden = hidden;
    lastVisibilityChangeMs = now;
}

/** Call when a reconnect triggers the map's bootstrap refetch. */
export function markMapReconnect(): void {
    reconnectCount++;
    lastReconnectMs = Date.now();
}

export function getMapLifecycleStats(): MapLifecycleStats {
    const now = Date.now();
    // Include time accrued in the current state so the numbers are correct even if
    // visibility has not changed recently.
    let visibleMs = mapVisibleMs;
    let hiddenMs = mapHiddenMs;
    if (isMapHidden !== null) {
        const elapsed = now - lastVisibilityChangeMs;
        if (isMapHidden) {
            hiddenMs += elapsed;
        } else {
            visibleMs += elapsed;
        }
    }

    const stats: MapLifecycleStats = {
        mapNavigationCount,
        mapVisibleMs: visibleMs,
        mapHiddenMs: hiddenMs,
        mapReconnectCount: reconnectCount,
    };
    if (isMapHidden !== null) {
        stats.wasMapHiddenAtFailure = isMapHidden;
    }
    if (lastReconnectMs !== null) {
        stats.msSinceLastReconnect = now - lastReconnectMs;
    }
    return stats;
}
